import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSend } from "react-icons/fi";
import { useAskSeQuestionMutation } from "../../../../../autogen/bff-api";
import { AttachmentDocumentOption, AttachmentPicker } from "../../../../../common/components/communication/AttachmentPicker";
import {
  formatDateTime,
  richTextHasContent,
} from "../../../../../common/components/communication/communicationUtils";
import { useMessageAttachments } from "../../../../../common/components/communication/useMessageAttachments";
import { useApiError } from "../../../../../common/errors/useApiError";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

export const SupplierAskQuestionForm = ({
  eventId,
  questionDeadline,
  deadlineIsRoundDeadline = false,
  eventIsOpen,
  isResolvingOpenRound = false,
  requestDocuments,
  offerDocuments,
  designContest = false,
}: {
  eventId: string;
  /** Null on private competitions — FOA § 14-2 does not apply, so questions stay open while the competition is. */
  questionDeadline: string | null;
  deadlineIsRoundDeadline?: boolean;
  eventIsOpen: boolean;
  isResolvingOpenRound?: boolean;
  requestDocuments: AttachmentDocumentOption[];
  offerDocuments?: AttachmentDocumentOption[];
  designContest?: boolean;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [ask, { isLoading }] = useAskSeQuestionMutation();
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2QuestionAttachment",
    pollRole: "portal",
  });

  // Advisory client-side check; the BFF rejects late questions authoritatively (409).
  const deadlinePassed = questionDeadline != null && new Date(questionDeadline).getTime() <= Date.now();
  const closed = deadlinePassed || !eventIsOpen;
  const canSubmit =
    subject.trim().length > 0 &&
    richTextHasContent(content) &&
    !attachments.isUploading &&
    !attachments.hasFailedUploads;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await ask({
        eventId,
        createSeQuestionRequest: { subject: subject.trim(), content, attachments: attachments.toInputs() },
      }).unwrap();
      setSubject("");
      setContent("");
      attachments.reset();
      setResetKey((key) => key + 1);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  // Only the closed verdict depends on the rounds: announcing a shut channel before they are known
  // is the very thing SM-1416 got wrong, but a competition still open on its own terms need not wait.
  if (closed && isResolvingOpenRound) return null;

  if (closed) {
    return (
      <Box
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        rounded="lg"
        p="4"
      >
        <Text fontWeight="semibold">
          {deadlinePassed ? t("Questions are closed") : t("Questions are not open")}
        </Text>
        <Text fontSize="sm" color="smMuted" mt="1">
          {deadlinePassed && questionDeadline != null
            ? `${t("The deadline for asking questions has passed")} (${formatDateTime(questionDeadline)}).`
            : t("The procurement is closed.")}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      rounded="lg"
      p="4"
    >
      <Stack spacing="0" mb="3">
        <Text fontWeight="semibold">{t("Ask a question")}</Text>
        {questionDeadline != null && (
          <Text fontSize="sm" color="smMuted">
            {deadlineIsRoundDeadline ? t("Deadline for this negotiation round") : t("Question deadline")}:{" "}
            {formatDateTime(questionDeadline)}
          </Text>
        )}
      </Stack>
      <Stack spacing="3">
        <FormControl isRequired>
          <FormLabel fontSize="sm">{t("Subject")}</FormLabel>
          <Input
            bg="smBackground"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">{t("Question")}</FormLabel>
          <TipTap
            key={resetKey}
            content=""
            editable
            debounceWait={0}
            onUpdate={(value) => setContent(value)}
          />
        </FormControl>
        <AttachmentPicker
          controls={attachments}
          requestDocuments={requestDocuments}
          offerDocuments={offerDocuments}
          designContest={designContest}
        />
        <Box textAlign="right">
          <Button
            leftIcon={<FiSend />}
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!canSubmit}
          >
            {t("Send question")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
